import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams
} from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, pluck, tap, shareReplay, take } from 'rxjs/operators';
import * as moment from 'moment';
import { ToastNotificationsService } from '../../components/notifications/toast-notifications/toast-notifications.service';

export interface IAzureAdSettings {
  Instance: string;
  Domain: string;
  TenantId: string;
  ClientId: string;
}

export interface IAppEndpoint {
  url: string;
  scopes: string[];
}

export interface IAppSettings {
  AzureAd: IAzureAdSettings;
  ApiEndpoint: IAppEndpoint;
  ApiEndpoint2?: IAppEndpoint;
  CommsEndpoint?: IAppEndpoint;
  CommsToken?: string;
  AppInitRequest?: string;
  SignalRConnectionUrl?: string;
  AppInsightsKey?: string;
  FullName?: string;

  Role?: string;
  ModuleName: string;
  UserName: string;
  VersionNumber: string;
}

@Injectable({ providedIn: 'root' })
export class ConfigurationService <K extends IAppSettings> {
  protected appSettings$: Observable<K>;
  protected values$ = new BehaviorSubject<K>(null);

  constructor(
    private http: HttpClient,
    private toastNotifications: ToastNotificationsService
  ) { }

  init(localConfig: K) {
    this.values$.next(localConfig);
  }

  processSettings(): Observable<K> {
    return this.fetchSettings().pipe(
      tap(v => this.toastNotifications.init(v.ModuleName))
    );
  }

  getAll(
    localConfig: K
  ): Observable<K> {
    this.init(localConfig);
    return this.processSettings();
  }

  get<T extends keyof K>(prop: T): Observable<K[T]> {
    return this.fetchSettings().pipe(pluck(prop));
  }

  getValue<T extends keyof K>(prop: T): K[T] {
    const appSettings = this.values$.getValue();

    return appSettings ? appSettings[prop] : null;
  }

  set<T extends keyof K>(prop: T, value: string): void {
    let config = this.values$.getValue();
    if (!config) {
      config = {} as K;
    }
    if (prop) {
      // eslint-disable-next-line
      // @ts-ignore
      config[prop] = value;

      this.values$.next(config);
      if (this.appSettings$) {
        this.appSettings$ = of(config);
      }
    }
  }

  setCookie(name: string, value: string, days: number): void {
    let expires = '';

    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = `; expires=${date.toUTCString()}`;
    }

    document.cookie = `${name}=${value}${expires}; path=/`;
  }

  getCookie(name: string): string {
    const nameEq = `${name}=`;
    const ca = document.cookie.split(';');

    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }

      if (c.indexOf(nameEq) === 0) {
        return c.substring(nameEq.length, c.length);
      }
    }
    return null;
  }

  eraseCookie(name: string): void {
    this.setCookie(name, '', -1);
  }

  protected fetchSettings(): Observable<K> {
    if (!this.appSettings$) {
      this.appSettings$ = this.requestSettings();
    }

    return this.appSettings$;
  }

  protected requestSettings(): Observable<K> {
    const configApiUrl = this.getValue('AppInitRequest');
    let values$: Observable<K>;
    if (configApiUrl) {
      const params = new HttpParams()
        .append('t', moment().format('x'))
        .append('hideSpinner', 'true');

      values$ = this.http
        .get<IAppSettings>(configApiUrl, {
          headers: new HttpHeaders({
            'Content-Type': 'application/json;charset=utf-8'
          }),
          params,
          responseType: 'json'
        })
        .pipe(
          map(v => Object.assign(v, this.values$.getValue())),
          tap(v => this.values$.next(v))
        );
    } else {
      values$ = of(this.values$.getValue());
    }

    return values$.pipe(shareReplay(1));
  }
}
